// The page's times: the clock over the panel, the cards' times and dates in
// the city's time zone (the world map's follows the city in view), ETAs,
// and the live dot and "updated" line under the clock.

import type { TrainStore } from '@tol/core/types';
import { el, esc } from './dom.ts';
import { renderLiveStatus } from './live-status.ts';

// Cards' times and the clock's.
const timeFormat = (timeZone: string, locale: string): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone });
const dateFormat = (timeZone: string, locale: string): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'short', day: 'numeric', timeZone });
const clockFormat = (timeZone: string, locale: string): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone });

export interface ClockOptions {
  readonly timeZone: string;
  readonly locale: string;
  // The server's time.
  readonly now: () => number;
  readonly live: TrainStore;
  // When the trains last came in (ShellState.lastUpdate; 0: not yet).
  readonly lastUpdate: () => number;
  // Data older than this (s) turns the live dot stale.
  readonly staleAfterS: number | ((group: number) => number);
  // "TfL's newest predictions", for the updated line's hover.
  readonly dataName: string;
}

export interface Clock {
  readonly fmtEta: (ms: number) => string;
  readonly clockTime: (ms: number) => string;
  readonly clockDate: (ms: number) => string;
  // The time zone from now on, and a label over the clock ("Sydney").
  readonly setTimeZone: (timeZone: string, locale: string, label?: string) => void;
  // Each second: the clock, and whether the feed is live.
  readonly tick: () => void;
}

export function createClock(options: ClockOptions): Clock {
  const { now, live } = options;
  function fmtEta(ms: number): string {
    const s = (ms - now()) / 1000;
    if (s <= 20) return 'Due';
    return `${Math.max(1, Math.round(s / 60))} min`;
  }
  let timeFmt = timeFormat(options.timeZone, options.locale);
  let dateFmt = dateFormat(options.timeZone, options.locale);
  const clockTime = (ms: number): string => timeFmt.format(new Date(ms));
  const clockDate = (ms: number): string => dateFmt.format(new Date(ms));

  let clockFmt = clockFormat(options.timeZone, options.locale);
  let clockLabel = '';
  const showClock = (): void => {
    const time = clockFmt.format(new Date(now()));
    if (clockLabel === '') el('#clock').textContent = time;
    else el('#clock').innerHTML = `<span class="clock-at">${esc(clockLabel)} </span>${esc(time)}`;
  };
  function setTimeZone(timeZone: string, locale: string, label = ''): void {
    timeFmt = timeFormat(timeZone, locale);
    dateFmt = dateFormat(timeZone, locale);
    clockFmt = clockFormat(timeZone, locale);
    clockLabel = label;
    showClock();
  }
  function tick(): void {
    showClock();
    const lastUpdate = options.lastUpdate();
    if (lastUpdate || live.failedGroups.length > 0) {
      // "Live" means our feed works: polls succeed and the data isn't
      // ancient. Snapshots are routinely a minute old, which doesn't matter:
      // ETAs are absolute times and positions are extrapolated.
      const s = Math.round((Date.now() - lastUpdate) / 1000);
      const oldest = live.groupTimes.size > 0 ? Math.min(...live.groupTimes.values()) : 0;
      const dataAge = oldest ? Math.round((now() - oldest) / 1000) : null;
      const limit = options.staleAfterS;
      const groupLimit = (group: number): number => (typeof limit === 'number' ? limit : limit(group));
      const stale =
        [...live.groupUpdates].some(
          ([group, at]: readonly [number, number]) => Math.round((Date.now() - at) / 1000) > groupLimit(group),
        ) ||
        [...live.groupTimes].some(
          ([group, at]: readonly [number, number]) => Math.round((now() - at) / 1000) > groupLimit(group),
        );
      renderLiveStatus(
        { updated: el('#updated'), dot: el('.live-dot') },
        {
          secondsSinceUpdate: lastUpdate ? s : null,
          failed: live.failedGroups.length > 0,
          dataAge,
          staleAfterS: typeof limit === 'number' ? limit : Number.POSITIVE_INFINITY,
          stale,
          dataName: options.dataName,
        },
      );
    }
  }
  return { fmtEta, clockTime, clockDate, setTimeZone, tick };
}
